import { createClient } from 'graphql-ws'
import { split, HttpLink } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { getMainDefinition } from '@apollo/client/utilities'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { ApolloClient, InMemoryCache, from } from '@apollo/client'
import { Observable } from 'apollo-link'
import { RetryLink } from '@apollo/client/link/retry'
import { resolveAuthToken, refreshAuthToken } from '../utils/authToken'

const authLink = setContext(async (_, { headers }) => {
	const token = await resolveAuthToken()
	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : '',
		},
	}
})

const retryLink = new RetryLink({
	delay: {
		initial: 300,
		max: Infinity,
	},
	attempts: {
		max: 3,
		retryIf: (error, _operation) => {
			console.log({ error })
			return error?.result?.errors[0]?.extensions?.code === 'UNAUTHENTICATED'
		},
	},
})

const promiseToObservable = (promise) =>
	new Observable((subscriber) => {
		promise.then(
			(value) => {
				if (subscriber.closed) return
				subscriber.next(value)
				subscriber.complete()
			},
			(err) => subscriber.error(err)
		)
	})

const refreshToken = async (operation) => {
	const oldHeaders = operation.getContext().headers
	const token = await refreshAuthToken()
	operation.setContext({
		headers: {
			...oldHeaders,
			authorization: token ? `Bearer ${token}` : '',
		},
	})
}

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
	if (
		graphQLErrors &&
		(['Unauthenticated', 'Invalid Auth Token']?.includes(graphQLErrors[0].message) || graphQLErrors[0]?.extensions?.code === 'UNAUTHENTICATED')
	) {
		return promiseToObservable(refreshToken(operation)).flatMap(() => forward(operation))
	}
})

const wsLink = new GraphQLWsLink(
	createClient({
		url: process.env.REACT_APP_BACKEND_WEB_SOCKET_URL,
	})
)

const httpLink = new HttpLink({
	uri: process.env.REACT_APP_BACKEND_URL,
})

const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query)
		return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
	},
	wsLink,
	httpLink
)

export const client = new ApolloClient({
	link: from([errorLink, retryLink, authLink, splitLink]),
	uri: process.env.REACT_APP_BACKEND_URL,
	cache: new InMemoryCache({
		typePolicies: {
			Query: {
				fields: {
					orders: {
						keyArgs: ['where', ['type']],
						merge(existing = [], incoming = []) {
							const merged = [...new Map([...existing, ...incoming].map((obj) => [JSON.stringify(obj), obj])).values()]
							return merged
						},
					},
					jobs: {
						keyArgs: ['where'],
						merge(existing = [], incoming = []) {
							const merged = [...new Map([...existing, ...incoming].map((obj) => [JSON.stringify(obj), obj])).values()]
							return merged
						},
					},
				},
			},
		},
	}),
})
